@import "stylesheets/sdx/typography/typography";
@import "stylesheets/sdx/link/link";

@mixin spacing {
  padding: $baseline-2;
  gap: $baseline-2;

  @include bp-desktop {
    padding: $baseline-3;
    gap: $baseline-3;
  }
}

.component {
  @include focus-visible;

  display: flex;
  flex-direction: column;
  height: 100%;
  background: $color-white;
  border: $border-width solid var(--disabled-state-color, #{$color-gray-tint-7});
  border-radius: $border-radius-large;
  overflow: hidden; // clip border-radius

  // for state cards
  &:not(.inline-notification)::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    border: var(--selected-border-width, 0) solid
      var(--input-checked-color, #{$color-gray-tint-7});
    pointer-events: none;
  }

  &.animated {
    transition:
      background-color 200ms ease-in-out,
      border-color 200ms ease-in-out,
      box-shadow 200ms ease-in-out;
  }

  &.notification,
  &.inline-notification {
    flex-direction: row;
    gap: $baseline-2;

    &:not(.has-label) {
      .label {
        display: none; // cancel gap
      }
    }

    &.alert {
      &::before {
        background: $color-int-red;
      }

      .icon {
        color: $color-int-red;
      }
    }

    &.warning {
      &::before {
        background: $color-int-orange;
      }

      .icon {
        color: $color-int-orange;
      }
    }

    &.general {
      &::before {
        background: $token-surface-interaction-default;
      }

      .icon {
        color: $token-text-color-interaction-default;
      }
    }

    &.confirmation,
    // deprecated
    &.confirm {
      &::before {
        background: $color-int-green;
      }

      .icon {
        color: $color-int-green;
      }
    }

    .first,
    .second {
      padding: unset;
    }

    .first {
      .icon {
        display: block;
        position: relative;
        left: 3px;
      }
    }

    .label {
      // Act like the .icon is there by setting it to "visibility: hidden;",
      // but make sure to create no visible "whole" by reversing the order and
      // with that placing the icon on the right side next to the text.
      flex-direction: row-reverse;
      justify-content: left;

      .icon {
        visibility: hidden;
      }
    }
  }

  &.notification {
    padding: $baseline-2;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 6px;
      height: 100%;
    }

    .icon {
      font-size: $icon-s2;
    }
  }

  &.inline-notification {
    border: unset;
    border-radius: unset;
    box-shadow: unset;
    background: unset;

    &:not(.has-label) {
      .slot {
        margin-top: 1px;

        // When there's no label, the slot "glides up". Align it to the icon.
        @include bp-desktop {
          margin-top: 4px;
        }
      }
    }
  }

  &.interaction,
  &.grey,
  &.orchid,
  &.blue {
    &:hover,
    &:focus-visible {
      .interaction-area > .link {
        @include link-hover;

        &.link--white {
          @include link-hover-dark;
        }
      }
    }
  }

  &.interaction {
    flex-direction: row;

    // override anchor defaults
    text-decoration: none;

    &:hover {
      border-color: $color-gray-tint-4;
      box-shadow: 0 4px 16px 0 rgb($color-gray, 0.16);
    }

    .second {
      gap: $baseline;
      flex-direction: column;
      align-self: end;
    }

    .image {
      width: 112px; // from figma
      height: 100%;

      @include bp-lg {
        width: 128px; // from figma
      }
    }

    .label {
      gap: $baseline-2;
    }

    @include bp-lg {
      &:not(.has-image) {
        .second {
          height: 100%;
        }

        .label {
          display: flex;
          flex-basis: 100%;
          flex-direction: column;
          justify-content: space-between;

          // reserve space for 2 lines title + icon + flexbox-gap
          min-height: 113px;
        }

        .flex-wrapper {
          flex-basis: 0;
        }
      }
    }

    &:not(.has-description),
    &:not(.has-label) {
      &:not(.has-image) {
        @include bp-md-max {
          // vertically center arrow
          .second {
            flex-direction: row;
            justify-content: space-between;
            align-self: auto;
            gap: 0;
          }

          .flex-wrapper {
            align-items: center;
            width: auto;
            height: auto;
          }
        }

        @include bp-lg {
          .label {
            // shift down to vertically align text and arrow
            margin: 0 $baseline-4 -36px 0;
          }
        }
      }

      &.has-image {
        .label {
          // shift down to vertically align text and arrow
          margin: 0 $baseline-4 -36px 0;
        }
      }
    }

    .icon {
      color: $color-sc-navy;
      font-size: $icon-s3;
    }

    .description {
      color: $color-gray;
      hyphens: auto;
      padding-right: $baseline;
    }

    .flex-wrapper {
      display: flex;
      align-items: flex-end;
      height: 100%;
      width: 100%;
      justify-content: space-between;
    }

    .first .icon {
      display: block;
      margin: $baseline-2 0 $baseline-2 $baseline-2;
      font-size: $icon-s2;

      @include bp-lg {
        display: none;
      }
    }

    .second .icon {
      display: none;

      @include bp-lg {
        display: block;
      }
    }

    &.has-image .second {
      flex-basis: auto;

      .icon {
        display: none;
      }

      .label {
        margin-top: $baseline-2;
      }
    }

    // small size
    &.small {
      // just on desktop
      @include bp-lg {
        &:not(.has-image) {
          .label {
            min-height: unset;
            flex-direction: unset;
            justify-content: unset;
          }
        }

        .first .icon {
          display: block;
          margin: $baseline-3 0 $baseline-3 $baseline-3;
          font-size: $icon-s3;
        }

        .second {
          .icon {
            display: none;
          }
        }
      }
    }
  }

  &.grey,
  &.orchid,
  &.blue {
    // override anchor defaults
    text-decoration: none;
  }

  &.orchid,
  &.blue {
    border-color: transparent;

    @include focus-visible--dark-background;
  }

  &.grey {
    background: $color-horizon;
    color: $color-gray;

    &:is(a):hover {
      box-shadow: 0 4px 16px 0 rgb($color-gray, 0.16);
    }
  }

  &.orchid {
    background: $color-orchid;

    &:is(a):hover {
      box-shadow: 0 4px 16px 0 rgb($color-orchid, 0.5);
    }
  }

  &.blue {
    background: $token-surface-interaction-default;

    &:is(a):hover {
      box-shadow: 0 4px 16px 0 rgb($token-surface-interaction-default, 0.5);
    }
  }

  &.split {
    flex-direction: column-reverse;

    @include bp-desktop {
      flex-direction: row;
    }

    &.gradient-1 {
      .first {
        @include gradient-1;
        color: $color-white;
      }
    }

    .first,
    .second {
      @include spacing;
      flex-basis: 50%;
    }
  }

  .first {
    .icon {
      display: none;
    }
  }

  .second {
    @include spacing;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .image {
    width: 100%;
    height: 180px;
    object-position: center;
    object-fit: cover;
    vertical-align: bottom;
    -webkit-user-drag: none; // Prevent conflict with dragging in Content Slider
  }

  .label {
    @include font-semi-bold;
    margin-bottom: 0;
    display: flex;
    gap: $baseline;
    color: $color-sc-navy;
    hyphens: auto;
  }

  .icon {
    font-size: $icon-s2;

    @include bp-desktop {
      font-size: $icon-s3;
    }
  }

  .slot {
    flex-grow: 1;
  }
}

.component.sdx--dark-theme {
  @include focus-visible--dark-theme;

  border-color: var(--disabled-state-color, #{$color-gray-tint-7-dark-theme});
  background-color: $color-dusk-tint-3-dark-theme;
  color: $color-gray-dark-theme;

  // Disable box-shadows for all layouts
  // (Add more specificity to override the :is(a) selector)
  &:hover:hover {
    box-shadow: none;
  }

  &:not(.inline-notification)::before {
    border: var(--selected-border-width, 0) solid
      var(--input-checked-color, #{$color-gray-tint-7-dark-theme});
  }

  &.notification,
  &.inline-notification {
    &.general {
      &::before {
        background-color: $color-int-blue-dark-theme;
      }

      .icon {
        color: $color-int-blue-dark-theme;
      }
    }

    &.confirmation,
    // deprecated
    &.confirm {
      &::before {
        background-color: $color-int-green-dark-theme;
      }

      .icon {
        color: $color-int-green-dark-theme;
      }
    }

    &.warning {
      &::before {
        background-color: $color-int-orange-dark-theme;
      }

      .icon {
        color: $color-int-orange-dark-theme;
      }
    }

    &.alert {
      &::before {
        background-color: $color-int-red-dark-theme;
      }

      .icon {
        color: $color-int-red-dark-theme;
      }
    }
  }

  // Override background color (dusk-tint-3) of default sdx-card
  &.inline-notification {
    &.alert,
    &.warning,
    &.general,
    &.confirmation,
    // deprecated
    &.confirm {
      background-color: transparent;
    }
  }

  &.interaction,
  &.grey,
  &.orchid,
  &.blue {
    &:hover,
    &:focus-visible {
      .link--standalone {
        @include link-hover-dark-theme;

        &.link--white {
          @include link-hover-dark;
        }
      }
    }
  }

  &.interaction {
    .description {
      color: $color-gray-dark-theme;
    }

    .icon {
      color: $color-gray-dark-theme;
    }

    &:hover {
      background-color: $color-dusk-tint-4-dark-theme;
      border-color: $color-gray-tint-4-dark-theme;
    }
  }

  &.grey {
    background-color: $color-dusk-tint-3-dark-theme;

    &:is(a):hover {
      background-color: $color-dusk-tint-4-dark-theme;
      border-color: $color-gray-tint-4-dark-theme;
    }
  }

  &.orchid,
  &.blue {
    border-color: transparent;
  }

  &.orchid {
    background-color: $color-orchid-dark-theme;

    &:is(a):hover {
      background-color: $color-orchid-tint-2-dark-theme;
    }
  }

  &.blue {
    background-color: $color-blue-dark-theme;

    &:is(a):hover {
      background-color: $color-blue-tint-2-dark-theme;
    }
  }

  .label {
    transition: color 200ms ease-in-out;
    color: $color-gray-dark-theme;
  }
}

// Styles for both light and dark theme that are deliberately put on the the
// bottom of the file for specificity (last override wins)
.component,
.component.sdx--dark-theme {
  &.dark,
  &.orchid,
  &.blue {
    &,
    .label,
    .first .icon {
      color: $color-white;
    }
  }
}
